import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PipelineTracker from "@/components/PipelineTracker";
import ActivityFeed from "@/components/panels/ActivityFeed";
import ListingPanel from "@/components/panels/ListingPanel";
import ScriptPanel from "@/components/panels/ScriptPanel";
import AgentPanel from "@/components/panels/AgentPanel";
import ClipsPanel from "@/components/panels/ClipsPanel";
import InfoSlidesPanel from "@/components/panels/InfoSlidesPanel";
import FinalPanel from "@/components/panels/FinalPanel";
import { toast } from "sonner";
import { ArrowLeft, Play, Loader2, AlertTriangle, Film, XCircle, Pencil, Check, X } from "lucide-react";

export default function Project() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("listing");
  const [running, setRunning] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const saveSlidesRef = useRef(null);

  const [notFound, setNotFound] = useState(false);
  const load = () =>
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch((e) => {
      if (e.response?.status === 404) setNotFound(true);
    });
  useEffect(() => {
    if (notFound) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id, notFound]);

  useEffect(() => {
    if (project?.kind === "cards") setTab((t) => (t === "listing" ? "slides" : t));
  }, [project?.kind]);

  useEffect(() => {
    if (project?.status === "setup_clips" || project?.status === "review_clips") setTab("clips");
  }, [project?.status]);

  const run = async () => {
    setRunning(true);
    try {
      // For cards reels the slides live in the InfoSlidesPanel's local state until saved.
      // Persist the current cards FIRST (marks any old render stale) so "Render cards reel"
      // never re-renders a previous version. No-op for non-cards projects.
      if (project?.kind === "cards" && saveSlidesRef.current) {
        await saveSlidesRef.current();
      }
      await api.post(`/projects/${id}/run`);
      toast.success("Pipeline started");
      setTimeout(load, 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const renderFinal = async () => {
    setRunning(true);
    try {
      await api.post(`/projects/${id}/clips/confirm`);
      toast.success("Rendering final video…");
      setTab("final");
      setTimeout(load, 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const cancelProject = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/projects/${id}/cancel`);
      toast.success("Stopped — reset to your last saved step.");
      setTimeout(load, 600);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const displayName = project?.name || project?.final?.title || project?.url || "Manual project";

  const startEditName = () => {
    setNameDraft(project?.name || project?.final?.title || "");
    setEditingName(true);
  };

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.post(`/projects/${id}/settings`, { name: nameDraft.trim() });
      toast.success("Project name updated");
      setEditingName(false);
      setTimeout(load, 400);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingName(false);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-zinc-950 px-6 text-center" data-testid="project-not-found">
        <AlertTriangle className="w-8 h-8 text-amber-500" />
        <div>
          <p className="text-lg font-semibold text-white">Project not found</p>
          <p className="mt-1 text-sm text-zinc-500">It may have been deleted, or it belongs to another account.</p>
        </div>
        <Button onClick={() => navigate("/app")} data-testid="not-found-back" className="rounded-full bg-amber-500 text-zinc-950 hover:bg-amber-400">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to studio
        </Button>
      </div>
    );

  if (!project)
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );

  const isRunning = project.status === "running";
  const isPickAgent = project.status === "pick_agent";
  const isReviewClips = project.status === "review_clips";
  const isSetupClips = project.status === "setup_clips";
  const isCards = project.kind === "cards";

  let actionBtn;
  if (isRunning) {
    actionBtn = (
      <Button data-testid="run-pipeline-button" disabled
        className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rendering…
      </Button>
    );
  } else if (isCards) {
    actionBtn = (
      <Button data-testid="render-cards-button" onClick={run} disabled={running}
        className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
        {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
        {project.final?.final_media_id ? "Re-render cards reel" : "Render cards reel"}
      </Button>
    );
  } else if (isPickAgent) {
    const noAvatar = project.settings?.use_agent === false;
    const canContinue = !!project.character_id || noAvatar;
    actionBtn = (
      <Button data-testid="continue-agent-button"
        onClick={canContinue ? run : () => setTab("agent")}
        disabled={running}
        className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
        {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
        {canContinue ? "Continue → Generate Clips" : "Cast an agent first"}
      </Button>
    );
  } else if (isSetupClips) {
    actionBtn = (
      <Button data-testid="setup-shots-button" onClick={() => setTab("clips")} disabled={running}
        className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
        <Film className="w-4 h-4 mr-2" /> Set up shots
      </Button>
    );
  } else if (isReviewClips) {
    actionBtn = (
      <Button data-testid="render-final-button" onClick={renderFinal} disabled={running}
        className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
        {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
        Render Final Video
      </Button>
    );
  } else {
    actionBtn = (
      <Button data-testid="run-pipeline-button" onClick={run} disabled={running}
        className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
        {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
        {project.status === "done" ? "Re-run" : "Run pipeline"}
      </Button>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 relative z-10">
      <header className="sticky top-0 z-20 bg-zinc-950/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate("/app")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Projects
          </button>
          <div className="flex items-center gap-3">
            {project.status && !["done", "created"].includes(project.status) && (
              <Button
                data-testid="cancel-project-button"
                onClick={cancelProject}
                disabled={running}
                variant="outline"
                className="h-10 rounded-sm border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {isRunning ? "Stop" : "Start over"}
              </Button>
            )}
            {actionBtn}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 self-start">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-2">
            {project.settings?.model} · {project.settings?.resolution}p · {project.settings?.aspect}
          </p>
          {editingName ? (
            <div className="mb-6 flex items-center gap-2" data-testid="project-name-editor">
              <input
                data-testid="project-name-input"
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                placeholder="Project name"
                className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded-sm px-2.5 py-1.5 text-lg font-display text-white focus:outline-none focus:border-amber-500"
              />
              <button data-testid="project-name-save" onClick={saveName} disabled={savingName}
                className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 shrink-0">
                {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button data-testid="project-name-cancel" onClick={() => setEditingName(false)} disabled={savingName}
                className="text-zinc-500 hover:text-zinc-300 disabled:opacity-50 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="mb-6 flex items-start gap-2 group/name">
              <h2 data-testid="project-title" className="font-display text-xl font-medium tracking-tight text-white break-words min-w-0">
                {displayName}
              </h2>
              <button data-testid="project-name-edit" onClick={startEditName} title="Rename project"
                className="mt-1 shrink-0 text-zinc-600 hover:text-amber-500 transition-colors opacity-0 group-hover/name:opacity-100">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {!isCards && <PipelineTracker pipeline={project.pipeline} onStageClick={() => {}} />}
          {isCards && (
            <div className="mt-4 text-xs text-zinc-500 border border-zinc-800 rounded-sm p-3">
              <span className="text-amber-500 font-medium">Cards reel</span> — build your cards, then hit <span className="text-amber-400">Render cards reel</span>. No property footage or avatar needed.
            </div>
          )}
          {project.error && (
            <div className="mt-4 flex items-start gap-2 text-xs text-red-400 border border-red-500/30 bg-red-500/10 rounded-sm p-3">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="break-words">{project.error}</span>
            </div>
          )}
          {project.status === "needs_manual" && (
            <div className="mt-4 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-sm p-3">
              Scrape blocked. Add listing details manually in the Listing tab.
            </div>
          )}
          {project.status === "review_photos" && (
            <div className="mt-4 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-sm p-3">
              Photos scraped — pick the ones to feature in the Listing tab, then continue.
            </div>
          )}
          {isPickAgent && (
            <div className="mt-4 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-sm p-3">
              Script ready — cast your agent in the Agent tab, then hit Continue.
            </div>
          )}
          {isReviewClips && (
            <div className="mt-4 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-sm p-3">
              Clips are ready. Review each scene, re-cut any you want, then Render Final Video.
            </div>
          )}
          <ActivityFeed projectId={project.id} status={project.status} />
        </aside>

        {/* Main */}
        <main>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1 flex-wrap h-auto">
              {(isCards ? ["slides", "final"] : ["listing", "script", "agent", "clips", "slides", "final"]).map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  data-testid={`tab-${t}`}
                  className="rounded-sm data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-950 font-mono2 text-[11px] uppercase tracking-[0.15em] px-4"
                >
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="listing" className="mt-6">
              <ListingPanel project={project} onChange={load} />
            </TabsContent>
            <TabsContent value="script" className="mt-6">
              <ScriptPanel project={project} />
            </TabsContent>
            <TabsContent value="agent" className="mt-6">
              <AgentPanel project={project} onChange={load} onContinue={run} />
            </TabsContent>
            <TabsContent value="clips" className="mt-6">
              <ClipsPanel project={project} onChange={load} onRender={renderFinal} />
            </TabsContent>
            <TabsContent value="slides" className="mt-6">
              <InfoSlidesPanel project={project} onChange={load} onRegisterSave={(fn) => { saveSlidesRef.current = fn; }} />
            </TabsContent>
            <TabsContent value="final" className="mt-6">
              <FinalPanel project={project} onGoToTab={setTab} onChange={load} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
